import { WorkPackage } from './Plan'
import { useContext, useEffect, useState } from 'react'
import { PlanBuilderContext } from './Plan'
import TaskObj from './TaskObj'
import { format } from 'date-fns'
import { dateformat } from '@/utils/GlobalConstants'
import { AddIcon, ChevronRightIcon, ChevronUpIcon, WorkPackageIcon } from '../ui/Icons/TooltipIcon'
import {CollisionPriority} from '@dnd-kit/abstract';
import { ArrowDown, ArrowDownUp, ArrowUp } from "lucide-react"
import { WorkstreamProgressChartRow } from '../WorkstreamProgressChart'
import GanttObj from './plan_gantt/GanttObj'
import { AppContext } from '@/App'
import { PageLockContext } from "../PageLockProvider";
import { useSortable } from '@dnd-kit/react/sortable'
import { TaskContextMenu } from './context_menus/task_menu'

interface WorkPackageProps {
  workPackage: WorkPackage
  selected?: boolean
  open?: boolean
  showChart?: boolean
  showGannt?: boolean
  index: number
  groupId: string
}

const WorkPackageObj = ({ workPackage, selected, open, showChart, showGannt, index, groupId }: WorkPackageProps) => {
  const app = useContext(AppContext);
  const props = useContext(PlanBuilderContext);
  const lock = useContext(PageLockContext);

  const [sortDuration, setSortDuration] = useState<"Asc" | "Desc" | "None">("None");
  const [sortAssignedTo, setSortAssignedTo] = useState<"Asc" | "Desc" | "None">("None");
  const [sortStartDate, setSortStartDate] = useState<"Asc" | "Desc" | "None">("None");

  const [newWorkPackageName, setNewWorkPackageName] = useState(workPackage.name);

  const { isDragging, ref: sortableRef } = useSortable({
    id: "wp" + workPackage.id,
    index: index,
    type: 'workPackage',
    accept: ['workPackage'],
    collisionPriority: CollisionPriority.Low,
    group: groupId,
    disabled: (props.multiSelect || app.authLevel < 4 || lock.viewOnly),
  });

  useEffect(() => {
    setNewWorkPackageName(workPackage.name);
  }, [workPackage.name]);

  function toggle(e: React.MouseEvent<HTMLButtonElement>) {
    props.toggleWorkPackage(workPackage.id);
    e.stopPropagation();
  }

  function getActualPercentage() {
    const totalTasks = workPackage.tasks.length;
    if (totalTasks === 0) return 0;
    const completedTasks = workPackage.tasks.filter(task => task.completed).length;
    return Math.round((completedTasks / totalTasks) * 100);
  }

  function getPlannedPercentage() {
    const totalTasks = workPackage.tasks.length;
    if (totalTasks === 0) return 0;
    const plannedTasks = workPackage.tasks.filter(task => new Date(task.finishDate).setHours(0, 0, 0) <= new Date().setHours(0, 0, 0)).length;
    return Math.round((plannedTasks / totalTasks) * 100);
  }

  function getWorkPackageFieldName() {
    if(selected && app.authLevel >= 4 && !props.multiSelect && !lock.viewOnly) {
      return <input id={workPackage.id + "_WorkPackageName"} type="text" className="ml-2 w-[365px] bg-transparent focus:outline-none" 
        value={newWorkPackageName} 
        maxLength={50}
        onChange={(e) => {setNewWorkPackageName(e.target.value)}}
        onBlur={(e) => {
          workPackage.name = e.target.value;
          props.updateWorkPackage(workPackage);
        }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }} />;
    }
    return <span className="ml-2 w-[365px]">{workPackage.name}</span>;
  }

  function sortByStartDate() {
    if (sortStartDate === "None") {
      setSortStartDate("Asc");
      setSortDuration("None");
      setSortAssignedTo("None");
      workPackage.tasks.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    }
    else if (sortStartDate === "Asc") {
      setSortStartDate("Desc");
      workPackage.tasks.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    } else {
      resetTaskOrder();
    }
    props.saveTaskOrder(workPackage.id);
  }

  function sortByAssignedTo() {
    const users = app.teamMembers.concat(app.projectManagers);

    if (sortAssignedTo === "None") {
      setSortAssignedTo("Asc");
      setSortStartDate("None");
      setSortDuration("None");
      workPackage.tasks.sort((a, b) => {
        const assigneeA = users.find(user => user.id === a.ownerId)?.displayName || '';
        const assigneeB = users.find(user => user.id === b.ownerId)?.displayName || '';
        return assigneeA.localeCompare(assigneeB);
      });
    }
    else if (sortAssignedTo === "Asc") {
      setSortAssignedTo("Desc");
      workPackage.tasks.sort((a, b) => {
        const assigneeA = users.find(user => user.id === a.ownerId)?.displayName || '';
        const assigneeB = users.find(user => user.id === b.ownerId)?.displayName || '';
        return assigneeB.localeCompare(assigneeA);
      });
    } else {
      resetTaskOrder();
    }

    props.saveTaskOrder(workPackage.id);
  }

  function sortByDuration() {
    if (sortDuration === "None") {
      setSortDuration("Asc");
      setSortStartDate("None");
      setSortAssignedTo("None");
      workPackage.tasks.sort((a, b) => {
        const durationA = new Date(a.finishDate).getTime() - new Date(a.startDate).getTime();
        const durationB = new Date(b.finishDate).getTime() - new Date(b.startDate).getTime();
        return durationB - durationA;
      });
    }
    else if (sortDuration === "Asc") {
      setSortDuration("Desc");
      workPackage.tasks.sort((a, b) => {
        const durationA = new Date(a.finishDate).getTime() - new Date(a.startDate).getTime();
        const durationB = new Date(b.finishDate).getTime() - new Date(b.startDate).getTime();
        return durationA - durationB;
      });
    } else {
      resetTaskOrder();
    }
    props.saveTaskOrder(workPackage.id);
  }

  function resetTaskOrder() {
    workPackage.tasks.sort((a, b) => a.id - b.id);
    setSortDuration("None");
    setSortAssignedTo("None");
    setSortStartDate("None");
    props.saveTaskOrder(workPackage.id);
  }

  return (
    <div ref={sortableRef} data-dragging={isDragging}>
      <div id={"wp" + workPackage.id} onClick={() => props.selectWorkPackage(workPackage.id)} className={'flex justify-between pl-4 text-planbuilder-workpackage-header-foreground ' + (selected ? "bg-planbuilder-workpackage-header" : "bg-planbuilder-workpackage-unselected") + " hover: cursor-pointer"}>
        <div className={"flex items-center " + (showGannt ? "w-[445px]" : "w-[465px]")}>
          <h2 className="flex items-center hover: cursor-pointer"><WorkPackageIcon className="m-2" /><span className={showGannt ? "w-[602px]" : "w-[642px] truncate"}>{getWorkPackageFieldName()}</span></h2>
        </div>
        {showChart &&
          <div className="flex flex-col min-w-[300px] w-full">
            <WorkstreamProgressChartRow planned={getPlannedPercentage()} actual={getActualPercentage()} actualColor='bg-green-600' plannedColor='bg-green-400' />
          </div>
        }
        {!showGannt && !showChart && open &&
          <div id="columnHeaders" className="flex grow flex-row space-x-14 justify-start items-center">
            <div onClick={(e) => { if(selected) {e.stopPropagation(); sortByDuration();}}}><div className='flex flex-row'>{(sortDuration === "Asc") ? <ArrowUp className='w-[15px] mr-2' /> : (sortDuration === "Desc") ? <ArrowDown className='w-[15px] mr-2'/> : <ArrowDownUp className='w-[15px] mr-2'/> }<span>Duration</span></div></div>
            <div onClick={(e) => { if(selected) {e.stopPropagation(); sortByAssignedTo()}}}><div className='flex flex-row'>{(sortAssignedTo === "Asc") ? <ArrowUp className='w-[15px] mr-2' /> : (sortAssignedTo === "Desc") ? <ArrowDown className='w-[15px] mr-2'/> : <ArrowDownUp className='w-[15px] mr-2'/> }<span>Owner</span></div></div>
          </div>
        }
        <div className='flex items-center'>
          {!showGannt &&
            <div className='flex items-center'>
              <div className="flex flex-col w-[190px] text-right mr-[10px]" onClick={(e) => { if(selected) {e.stopPropagation(); sortByStartDate()}}}><div className='flex flex-row'>{open && ((sortStartDate === "Asc") ? <ArrowUp className='w-[15px]' /> : (sortStartDate === "Desc") ? <ArrowDown className='w-[15px]'/> : <ArrowDownUp className='w-[15px]'/> )}<span className='ml-auto'>{format(workPackage.startDate, dateformat)} - {format(workPackage.finishDate, dateformat)}</span></div></div>
              {app.authLevel >= 4 && !lock.viewOnly ? (
              <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.addTask(workPackage.id); }} className="p-0 bg-transparent border-0">
                <AddIcon className="m-2" tooltip="Add Task" />
              </button>)
              : <div className="m-2 w-[25px]" />}
            </div>}
          {workPackage.tasks.length != 0 && <button onClick={toggle} className="px-2 py-2 h-9">{open ? <ChevronUpIcon /> : <ChevronRightIcon />}</button>}
        </div>
        {showGannt &&
          <GanttObj
            startDate={workPackage.startDate}
            finishDate={workPackage.finishDate}
            color={selected ? "bg-planbuilder-workpackage-header" : "bg-planbuilder-workpackage-unselected"}
          />
        }
      </div>
      <div className={(open ? "" : " hidden")}>
        {workPackage.tasks.map((task, index) => (
          <TaskContextMenu key={"tcm" + task.id}>
            <TaskObj
              key={"t" + task.id}
              task={task}
              selected={props.multiSelect ? props.selectedTaskIDs.includes(task.id) : props.selectedTaskID === task.id}
              primary={props.multiSelect ? props.selectedTaskID === task.id : false}
              showGantt={showGannt}
              index={index}
              subTaskDepth={0}
              groupId={"wp" + workPackage.id}
            />
          </TaskContextMenu>
        ))}
      </div>
    </div>
  )
}

export default WorkPackageObj